import Keypad from "./KeyPad";
import { useCallback, useEffect, useRef, useState } from "react";
import { IProblem, IProblemGenerator } from "src/lib/ProblemGenerators/sharedTypes";
import ProgressBar from "./ProgressBar";
import CombinedProblemGenerator from "src/lib/ProblemGenerators/CombinedProblemGenerator";
import History from "./History";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import Settings, { CompleteSettings, defaultCompleteConfigs } from "./Settings";
import Button from "./Button";
import { QuestionSetProblemGenerator } from "src/lib/ProblemGenerators/QuestionSetProblemGenerator";
import { checkStructure } from "src/lib/utils/configUtils";
import { convertConfigs } from "src/lib/ProblemGenerators/ProgressionDefaults";
import { addToStats } from "src/lib/Stats/statsMain";

let savedSettings = localStorage.getItem("settings") === null ? defaultCompleteConfigs : JSON.parse(localStorage.getItem("settings") as string) as CompleteSettings;
console.log(`Loaded saved Settings: `, savedSettings)

if (!checkStructure(savedSettings, defaultCompleteConfigs, "root")) {
    console.log(`Mismatched settings, setting to default`)
    localStorage.setItem("settings", JSON.stringify(defaultCompleteConfigs));
    savedSettings = defaultCompleteConfigs;
}

export default function PracticeTest() {
    const problemGenerator = useRef<IProblemGenerator | undefined>(undefined);
    const [ problems, setProblems ] = useState<IProblem[]>([]);
    const [ problem, setProblem ] = useState<IProblem | undefined>();
    const [ wasCorrect, setWasCorrect ] = useState(true);
    const [ startTime, setStartTime ] = useState(0);
    const [ endTime, setEndTime ] = useState(0);
    const [ heartbeat, setHeartbeat ] = useState(0);
    const updateTimer = useRef<NodeJS.Timeout>(undefined);
    const testTimer = useRef<NodeJS.Timeout>(undefined);
    const [ showSettings, setShowSettings] = useState(false);
    const [ configs, setConfigs ] = useState(savedSettings);
    const [ resetKeypad, setResetKeypad] = useState(0);
    const [ isTestRunning, setIsTestRunning ] = useState(false);
    const [ isTestReady, setIsTestReady ] = useState(true);
    const problemStartTime = useRef(0);
    
    const stopTimer = useCallback(() => {
        if (updateTimer) {
            clearInterval(updateTimer.current);
            updateTimer.current = undefined;
        }

        if (testTimer) {
            clearTimeout(testTimer.current);
            testTimer.current = undefined;
        }
    }, [ updateTimer ])

    const endTest = useCallback(() => {
        setIsTestRunning(false);
        stopTimer();
        setEndTime(Date.now());
    }, [ setIsTestRunning, stopTimer, setStartTime ])

    const startTimer = useCallback(() => {
        setStartTime(Date.now())
        updateTimer.current = setInterval(() => {
            setHeartbeat((value) => value + 1);
        }, 200)
        if (configs.topLevel.endCondition === "timeLimit") {
            testTimer.current = setTimeout(() => {
                console.log(`Ending test!`)
                endTest();
            }, configs.topLevel.timeLimit * 1000);
        }
        setIsTestRunning(true);
        setIsTestReady(false);
        problemStartTime.current = Date.now();
    }, [setHeartbeat, setStartTime, configs.topLevel.timeLimit, setIsTestRunning, setIsTestReady, endTest, problemStartTime ])

    const restart = useCallback(() => {
        setEndTime(0);
        setIsTestRunning(false);
        stopTimer();
        setStartTime(0);

        setProblems([]);
        resetProblemGenerator();
        if (problemGenerator.current)
            setProblem(problemGenerator.current.getNext());
        setWasCorrect(true);
        setResetKeypad((value) => value + 1);
        setIsTestReady(true);
        setIsTestRunning(false);
    }, [ setProblems, setProblem, setWasCorrect, stopTimer, setStartTime, setResetKeypad, setIsTestReady, setIsTestRunning, setEndTime ]);

    const saveConfigs = useCallback((configs: CompleteSettings) => {
        localStorage.setItem("settings", JSON.stringify(configs) );
        setConfigs(configs);
        restart()
    }, [ setConfigs, restart ])

    const checkAnswer = useCallback((answer: number) => {
        let wasCorrect = problem?.correctAnswer === answer;
        setWasCorrect(wasCorrect);

        const problemOut = Object.assign({}, problem, { userAnswer: answer, timeToAnswer: Date.now() - problemStartTime.current });
        console.log(problemOut);

        addToProblems(problemOut);
        
        if (problem) {
            addToStats(problemOut, wasCorrect);
        }

        if (problemGenerator?.current?.reportResult)
            problemGenerator.current.reportResult(wasCorrect, answer);

        getNewProblem();

        if (configs.topLevel.endCondition === "totalQuestions") {
            if (problems.length + 1 >= configs.topLevel.totalQuestions ) {
                endTest();
            }
        }

    }, [ problem, setProblem, setWasCorrect, configs.topLevel, problemGenerator, endTest ]);

    const addToProblems = useCallback((problem: IProblem) => {
        setProblems((value) => [ ...value, problem ]);
    }, [ setProblems ])

    const getNewProblem = useCallback(() => {
        if (problemGenerator.current !== undefined) {
            setProblem(problemGenerator.current.getNext());
            problemStartTime.current = Date.now();
        }
    }, [ setProblem, problemStartTime ])

    const resetProblemGenerator = useCallback(() => {
        switch (configs.topLevel.problemSelection) {
            case "custom":
                problemGenerator.current = new CombinedProblemGenerator(configs.generatorSettings);
                break;
            case "setSelection":
                problemGenerator.current = new QuestionSetProblemGenerator(configs.setSelection);
                break;
            case "progressive":
                const convertedConfigs = convertConfigs(configs.progression);
                console.log(`Converted Configs: `, convertedConfigs)
                problemGenerator.current = new CombinedProblemGenerator(convertedConfigs)
                break;
            default:
                console.log(`Unknown problem selection method: ${configs.topLevel.problemSelection}`)
        }

        getNewProblem();

    }, [ configs ])

    useEffect(() => {
        console.log(`Setting problem generator to: ${configs.topLevel.problemSelection}`);
        switch (configs.topLevel.endCondition) {
            case "timeLimit":
                break;
            case "totalQuestions":
                break;
        }

        resetProblemGenerator();

    }, [ configs, getNewProblem, resetProblemGenerator ])

    useEffect(() => {
        setConfigs(savedSettings)
//        setProblem(problemGenerator.current.getNext());
    }, [])

    
    const correct = problems.reduce((prev, current) => { return prev + (current.userAnswer === current.correctAnswer ? 1 : 0)}, 0);

    // Progress bar display
    const totalBar = configs.topLevel.endCondition === "timeLimit" ? configs.topLevel.timeLimit : configs.topLevel.totalQuestions;
    const filledBar = startTime === 0 ? 0 : (configs.topLevel.endCondition === "timeLimit" ? Math.round(((!isTestRunning && !isTestReady ? endTime : Date.now()) - startTime)/1000) : problems.length);
    const finishText = configs.topLevel.endCondition === "totalQuestions" ? `FINISHED in ${formatTime(startTime)}!` : `FINISHED!`

    let problemText = "";

    if (!isTestReady && !isTestRunning)
        problemText = finishText
    else if (isTestReady && !isTestRunning)
        problemText = "Click Start to Begin";
    else
        problemText = problem?.display ?? ""

    return (
        <>
            <Settings 
                show={showSettings} 
                currentConfigs={configs} 
                saveClicked={(newConfigs) => { console.log(`Save Clicked`); saveConfigs(newConfigs); setShowSettings(false);}} 
                cancelClicked={() => { setShowSettings(false)}}
            />
            <div className="flex flex-row h-full overflow-hidden">
                <div className="flex-grow flex flex-col h-full p-4 border-none md:border-r max-w-4xl overflow-hidden">
                    <div className="flex flex-row gap-4">
                        <Button otherClasses="px-4" disabled={!(isTestReady || !isTestRunning)} onClick={() => { if (isTestReady || !isTestRunning) setShowSettings(true)}} ><FontAwesomeIcon icon={faGear}/></Button>
                        <ProgressBar displayMethod={configs.topLevel.endCondition === "timeLimit" ? "timer" : "questionCount"} totalProblems={totalBar} problemsComplete={filledBar} color={wasCorrect ? "bg-green-300" : "bg-red-300"}/>
                        { !isTestReady && <Button disabled={isTestReady} onClick={() => { restart()}}>Reset</Button> }
                        { isTestReady && <Button otherClasses="bg-green-500" onClick={() => { startTimer()}}>Start</Button> }
                    </div>
                    <div className="h-30">
                        <div className="font-bold text-3xl md:text-7xl py-4">{problemText}</div>
                        <div className="font-bold text-2xl" style={{ display: !isTestReady && !isTestRunning ? "inline-block" : "none"}}>{correct}/{problems.length} Correct</div>
                    </div>
                    <div className="block md:hidden text-xl font-bold text-slate-500">
                        History
                    </div>
                    <div className="flex-grow block md:hidden overflow-hidden h-full">
                        <History problems={problems} doScroll={true}/>    
                    </div>
                    <div className={`flex flex-row gap-4 w-full md:p-0 md:block ${ !isTestRunning && !isTestReady ? "hidden" : "block"}`}>
                        <Keypad 
                            key={resetKeypad}
                            onOKClicked={(answer: number) => {checkAnswer(answer)}} 
                            trigger={() => { if (!updateTimer.current) startTimer()}}
                            disabled={!isTestRunning && !isTestReady} />
                    </div>
                </div>
                <div className="hidden md:block flex-grow flex flex-col min-w-20 p-4">
                    <div className="font-bold text-xl">History</div>
                    <History problems={problems} doScroll={true}/>
                </div>
            </div>
        </>
    )
}

function formatTime(startTime: number)
{
    if (startTime === 0)
        return "Paused"
    const elapsed = (Date.now() - startTime)/1000;
    const minutes = Math.floor(elapsed/60);
    const seconds = Math.floor(elapsed % 60);

    //console.log(`Elapsed ${elapsed} --  minutes: ${minutes} - seconds: ${seconds}`)
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
